import type { SolvePhase } from "@playbookai/types";
import { clampResize, type ContainerPadding, type Rect } from "@playbookai/ui";
import {
  defaultSolvePhaseAreaRect,
  resolveSolvePhaseAreaLayout,
  solvePhaseAreaContentOrigin,
  type SolvePhaseAreaEntry,
} from "@playbookai/widget-schemas";
import {
  CANVAS_HEIGHT,
  HEADER_HEIGHT,
  LANE_PADDING_X,
  LANE_PADDING_Y,
  LANE_WIDTH,
  PHASE_CONTENT_HEIGHT,
} from "./phase-metadata.js";

/**
 * Pure geometry for SOLVE's resizable phase areas.
 *
 * Data-blind and dependency-free apart from the shared DS geometry kernel: no
 * React, no Yjs. The persistence layer (`phase-area-doc-shape.ts`) supplies
 * plain entries and receives plain rects, so every rule here is unit-testable
 * without a document.
 *
 * Three conventions are load-bearing:
 *
 *  - **An absent entry renders exactly the historical fixed lane.** A SOLVE
 *    canvas that predates this feature stores nothing, so `layoutPhaseAreas`
 *    collapses to `phaseIndex * LANE_WIDTH` — byte-identical to what the five
 *    fixed swimlanes drew before. Prior layouts are preserved by construction,
 *    not by a migration.
 *  - **An area never shrinks past the widgets it holds.** The floor is the DS
 *    `clampResize` probed with a zero-size rect, exactly as VSM's phase
 *    containers derive theirs. Membership is the widget's STORED `phase` — the
 *    same authority the lock gate and the placement upsert use — never its
 *    coordinate.
 *  - **Areas are a left-to-right row and never overlap.** Reading pushes an
 *    area to its predecessor's right edge when a stored rect would otherwise
 *    sit behind it, and a width resize is capped at the next revealed area's
 *    left edge. Nothing else moves: resizing one area never repositions
 *    another's contents.
 *
 * `y` is 0 for every area by construction. An area is a full-height column
 * anchored to the canvas top because its header card is drawn there; nothing
 * moves an area vertically, so no `y` is stored.
 */

/**
 * The persisted size of one phase area. `y` is 0 by construction.
 *
 * An alias, not a second declaration: the shared placement rule in
 * `@playbookai/widget-schemas` reads the same geometry, and two structurally
 * identical entry types would be the first place the frontend and the backend
 * could drift.
 */
export type PhaseAreaEntry = SolvePhaseAreaEntry;

/** Rect per revealed phase, in canvas coordinates. */
export type PhaseAreaLayout = ReadonlyMap<SolvePhase, Rect>;

/** Stored entries, keyed by phase. Every key is optional — absence is normal. */
export type PhaseAreaEntries = Readonly<
  Partial<Record<SolvePhase, PhaseAreaEntry>>
>;

/**
 * Space left between an area and the next one REVEALED after this feature
 * shipped. Areas that predate it stay flush, because nothing moves them — the
 * gap describes where a new area is placed, not a spacing the row is
 * normalized to.
 */
export const SOLVE_PHASE_AREA_GAP = 48;

/**
 * The floor an EMPTY area may be dragged to. `clampResize` returns its input
 * untouched when there are no occupants, so probing an empty area with a
 * zero-size rect would otherwise yield 0×0 and let a lane be collapsed until
 * its header card disappeared. Width is sized so the header card (20px inset +
 * 36px letter circle + 12px gap + label + the 28px lock control + 20px inset)
 * still reads.
 */
export const MIN_PHASE_AREA = { w: 360, h: 320 } as const;

/** Pointer-drag snap, in canvas px. Mirrors VSM's lane resize granularity. */
export const PHASE_AREA_SNAP = 8;

/** Keyboard resize step, and the coarse step Shift selects. */
export const PHASE_AREA_KEYBOARD_STEP = 40;
export const PHASE_AREA_KEYBOARD_COARSE_STEP = 160;

/**
 * Inset kept between an area edge and its nearest widget. `pad` matches the
 * lane's own content inset; `headerPad` reserves the header band. Only `pad`
 * reaches `clampResize` (it constrains `w`/`h` alone), but the pair is declared
 * together so a future top-anchored rule reads the same source.
 */
export const SOLVE_PHASE_AREA_PADDING: ContainerPadding = {
  pad: LANE_PADDING_X,
  headerPad: HEADER_HEIGHT + LANE_PADDING_Y,
};

/**
 * The historical fixed lane for a phase — what an area with no stored entry is.
 *
 * Delegates: Player AI resolves the same lane server-side, so the rule has one
 * implementation (`@playbookai/widget-schemas`) rather than a copy per caller.
 */
export function defaultPhaseAreaRect(phase: SolvePhase): Rect {
  return defaultSolvePhaseAreaRect(phase);
}

/**
 * The rect of every revealed area, in reveal order.
 *
 * A stored entry is authoritative for `w`/`h`. Its `x` is honoured unless it
 * would sit behind the previous area's right edge, in which case it is pushed
 * flush — so two clients that concurrently widen one area and reveal the next
 * can never render an overlap. With nothing stored the `max` is an identity
 * (each default rect starts exactly where its predecessor ends), which is what
 * keeps a pre-feature canvas pixel-identical.
 *
 * The gap is deliberately NOT applied here. Reading must not move an area that
 * a writer never placed, or every existing canvas would shift right on open.
 *
 * Delegates to `@playbookai/widget-schemas` so the rendered layout and the area
 * Player AI places a widget against are the same arithmetic. They were NOT
 * before: the backend read each phase's stored `x` in isolation and fell back to
 * the fixed lane per phase, so an unstored area following a widened one resolved
 * behind it and an AI widget landed inside its neighbour.
 */
export function layoutPhaseAreas(
  phases: readonly SolvePhase[],
  entries: PhaseAreaEntries,
): PhaseAreaLayout {
  return resolveSolvePhaseAreaLayout(phases, entries);
}

/**
 * The canvas dimensions the areas occupy.
 *
 * Both axes floor at the historical revealed-prefix bounds — `n · LANE_WIDTH`
 * wide and `CANVAS_HEIGHT` tall — so SHRINKING an area can never contract the
 * canvas. Two things depend on that floor, and both fail destructively without
 * it: `WidgetLayer`/`StickyNoteLayer` clamp a drag into `[0, canvas − size]`, so
 * content already outside a narrowed canvas would snap inward on its next drag;
 * and SOLVE hides a phase-LESS widget or a sticky note whose `x` is past the
 * canvas width, so a shrink would make notes to the right of the new edge
 * silently disappear. Growth is unbounded on both axes.
 */
export function phaseAreaExtent(layout: PhaseAreaLayout): {
  w: number;
  h: number;
} {
  let w = layout.size * LANE_WIDTH;
  // Annotated: `SOLVE_CANVAS_BOUNDS` is const-asserted, so `CANVAS_HEIGHT`
  // carries the non-fresh literal type `2000` and `let h = CANVAS_HEIGHT`
  // would infer that literal rather than widening to `number`.
  let h: number = CANVAS_HEIGHT;
  for (const rect of layout.values()) {
    w = Math.max(w, rect.x + rect.w);
    h = Math.max(h, rect.h);
  }
  return { w, h };
}

/**
 * The smallest `w`/`h` this area may be resized to without clipping a widget it
 * holds. Probes the DS `clampResize` with a zero-size rect at the area's real
 * origin — the clamp's output IS the floor, the same technique VSM's
 * `contentsFloorSize` uses.
 *
 * Occupants are the widgets whose STORED `phase` is this one. Sticky notes are
 * deliberately excluded: they carry no phase at all, so their membership would
 * be positional and transient, and a note dragged across the board would make
 * an area's floor jump. The approved behaviour names widgets, not notes.
 */
export function phaseAreaFloor(
  rect: Rect,
  occupants: readonly Rect[],
): { w: number; h: number } {
  if (occupants.length === 0) return { ...MIN_PHASE_AREA };
  const floored = clampResize(
    { ...rect, w: 0, h: 0 },
    occupants,
    SOLVE_PHASE_AREA_PADDING,
  );
  return {
    w: Math.max(MIN_PHASE_AREA.w, Math.ceil(floored.w)),
    h: Math.max(MIN_PHASE_AREA.h, Math.ceil(floored.h)),
  };
}

/**
 * Clamp a requested size to the contents floor.
 *
 * Growth is NOT capped: widening an area pushes the areas after it right
 * instead of stopping at the neighbour (`planPhaseAreaResize`). The floor is the
 * only bound, and it is applied on BOTH channels — as the live bound of a
 * pointer gesture and again on the write path — so the pointer and keyboard
 * routes cannot disagree about where an area stops.
 */
export function clampPhaseAreaSize(
  rect: Rect,
  occupants: readonly Rect[],
  requested: { w: number; h: number },
): { w: number; h: number } {
  const floor = phaseAreaFloor(rect, occupants);
  return {
    w: Math.round(Math.max(floor.w, requested.w)),
    h: Math.round(Math.max(floor.h, requested.h)),
  };
}

/** Canvas content that travels with the areas a width change pushes. */
export interface PhaseAreaCarry {
  /**
   * Signed offset every pushed area and its contents move by. Positive when the
   * resized area grew, negative when it shrank — a shrink pulls the row back so
   * the gaps between areas stay exactly what they were.
   */
  readonly dx: number;
  /** Phases whose widgets travel, by their STORED `phase`. */
  readonly phases: readonly SolvePhase[];
  /**
   * The resized area's OLD right edge. Content with no phase to travel by — a
   * phase-agnostic widget, a sticky note — travels when its centre is at or
   * past this, which is the same centre rule VSM uses for container occupants.
   */
  readonly fromX: number;
}

/** Everything one resize changes, computed before anything is written. */
export interface PhaseAreaResizePlan {
  /** Every area to write: the resized one first, then each pushed area. */
  readonly rects: ReadonlyArray<readonly [SolvePhase, PhaseAreaEntry]>;
  readonly carry: PhaseAreaCarry;
}

/**
 * Plan a resize: the clamped size for this area, the offset every later area
 * moves by, and the content that travels with them. Returns `null` when the
 * clamped size is the size it already has, so an idempotent gesture writes
 * nothing at all.
 *
 * Widening pushes rather than capping (user decision, 2026-09-13). Three
 * properties follow, and all three are what make pushing safe:
 *
 *  - **Relative layout is preserved exactly.** Every later area and every piece
 *    of content it holds moves by the SAME delta, so gaps, column alignment and
 *    the spacing between widgets all survive untouched.
 *  - **No widget is resized.** Only `x` moves. An area resize and a widget
 *    resize stay different things; SOLVE has no widget resize at all.
 *  - **Overlap is unreachable.** The row is translated, not re-packed, so the
 *    only way two areas could meet is a concurrent edit — which the read-time
 *    push in `layoutPhaseAreas` still absorbs.
 *
 * Height changes carry nothing: areas have no vertical neighbour.
 */
export function planPhaseAreaResize(
  layout: PhaseAreaLayout,
  phases: readonly SolvePhase[],
  phase: SolvePhase,
  occupants: readonly Rect[],
  requested: { w: number; h: number },
): PhaseAreaResizePlan | null {
  const index = phases.indexOf(phase);
  const rect = layout.get(phase);
  if (index < 0 || !rect) return null;
  const size = clampPhaseAreaSize(rect, occupants, requested);
  const dx = size.w - rect.w;
  if (dx === 0 && size.h === rect.h) return null;

  const later = phases.slice(index + 1);
  const rects: Array<readonly [SolvePhase, PhaseAreaEntry]> = [
    [phase, { x: rect.x, w: size.w, h: size.h }],
  ];
  if (dx !== 0) {
    for (const pushed of later) {
      const pushedRect = layout.get(pushed);
      if (!pushedRect) continue;
      rects.push([
        pushed,
        { x: pushedRect.x + dx, w: pushedRect.w, h: pushedRect.h },
      ]);
    }
  }
  return {
    rects,
    carry: { dx, phases: dx === 0 ? [] : later, fromX: rect.x + rect.w },
  };
}

/**
 * The phases whose content a width change would move. A caller must be allowed
 * to edit every one of them, not just the area being dragged: the push carries
 * their widgets, and a phase lock exists precisely to stop that. This mirrors
 * the `additionalPhases` deny-on-any rule the REST canvas gate already applies
 * to a widget move that touches two phases (ADR-0019).
 */
export function phasesAffectedByWidthResize(
  phases: readonly SolvePhase[],
  phase: SolvePhase,
): readonly SolvePhase[] {
  const index = phases.indexOf(phase);
  if (index < 0) return [];
  return phases.slice(index + 1);
}

/**
 * Where a newly revealed area goes: to the RIGHT of the phase before it, at
 * that area's ACTUAL current right edge plus the gap — never at a fixed
 * index-derived offset, which is what made a revealed lane land on top of a
 * widened one. It inherits its predecessor's height so the row stays even.
 */
export function revealedPhaseAreaRect(previous: Rect): PhaseAreaEntry {
  return {
    x: previous.x + previous.w + SOLVE_PHASE_AREA_GAP,
    w: LANE_WIDTH,
    h: previous.h,
  };
}

/**
 * The first widget slot inside an area — the lane-relative canonical origin.
 *
 * Delegates for the same reason the layout does: this is where a manually added
 * widget starts and where an AI-created one starts, and one inset constant has to
 * serve both.
 */
export function phaseAreaContentOrigin(rect: Rect): { x: number; y: number } {
  return solvePhaseAreaContentOrigin(rect);
}

/**
 * The rect the phase strip flies the camera to. Height stays capped at the
 * nominal content height so a jump frames the working area rather than the
 * full column, matching `phaseRect`'s long-standing behaviour.
 */
export function phaseAreaFitRect(rect: Rect): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return {
    x: rect.x,
    y: 0,
    width: rect.w,
    height: Math.min(rect.h, PHASE_CONTENT_HEIGHT),
  };
}
